import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { type Conversation } from "shared/apiClient";
import { AnalysisReportEvents } from "shared/types";
import { type Noticeboard } from "shared/lib";
import { EventTypes } from "@/types/events";

import {
  useIsReportGenerating,
  useIsReportReGenerating,
} from "@/components/analysisLayout/hooks";

import { AnalysisSidebar, AnalysisSidebarMenuItems } from "./AnalysisSidebar";

export interface AnalysisSidebarContainerProps {
  conversation: Conversation | undefined | null;
  messages: Conversation["messages"];
  noticeboardData: Noticeboard | undefined | null;
  subTitle: string;
  title: string;
}

export function AnalysisSidebarContainer({
  conversation,
  messages,
  noticeboardData,
  subTitle,
  title,
}: AnalysisSidebarContainerProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedMenuItem = (searchParams.get("selectedMenuItem") ||
    "") as AnalysisSidebarMenuItems;

  const isReportGenerating = useIsReportGenerating(conversation);
  const isReportReGenerating = useIsReportReGenerating(conversation);

  useEffect(() => {
    const scrollToTopEvent = new CustomEvent(EventTypes.ScrollToTop);
    document.dispatchEvent(scrollToTopEvent);
  }, [selectedMenuItem]);

  useEffect(() => {
    if (!selectedMenuItem) {
      setSearchParams((prev) => {
        prev.set(
          "selectedMenuItem",
          AnalysisSidebarMenuItems.PERMIT_REQUIREMENTS,
        );
        return prev;
      });
    }
  }, [selectedMenuItem, setSearchParams]);

  useEffect(() => {
    const handleShowPermitRequirementsEvent = () => {
      setSearchParams((prev) => {
        prev.set(
          "selectedMenuItem",
          AnalysisSidebarMenuItems.PERMIT_REQUIREMENTS,
        );
        return prev;
      });
    };

    document.addEventListener(
      AnalysisReportEvents.SHOW_PERMIT_REQUIREMENTS,
      handleShowPermitRequirementsEvent,
    );

    const handleShowApplicationRequirementsEvent = () => {
      setSearchParams((prev) => {
        prev.set(
          "selectedMenuItem",
          AnalysisSidebarMenuItems.APPLICATION_REQUIREMENTS,
        );
        return prev;
      });
    };

    document.addEventListener(
      AnalysisReportEvents.SHOW_APPLICATION_REQUIREMENTS,
      handleShowApplicationRequirementsEvent,
    );

    const handleShowLotAndProposalEvent = () => {
      setSearchParams((prev) => {
        prev.set("selectedMenuItem", AnalysisSidebarMenuItems.LOT_AND_PROPOSAL);
        return prev;
      });
    };

    document.addEventListener(
      AnalysisReportEvents.SHOW_LOT_AND_PROPOSAL,
      handleShowLotAndProposalEvent,
    );

    const handleShowNextStepsEvent = () => {
      setSearchParams((prev) => {
        prev.set("selectedMenuItem", AnalysisSidebarMenuItems.NEXT_STEPS);
        return prev;
      });
    };

    document.addEventListener(
      AnalysisReportEvents.SHOW_NEXT_STEPS,
      handleShowNextStepsEvent,
    );

    return () => {
      document.removeEventListener(
        AnalysisReportEvents.SHOW_PERMIT_REQUIREMENTS,
        handleShowPermitRequirementsEvent,
      );

      document.removeEventListener(
        AnalysisReportEvents.SHOW_APPLICATION_REQUIREMENTS,
        handleShowApplicationRequirementsEvent,
      );

      document.removeEventListener(
        AnalysisReportEvents.SHOW_LOT_AND_PROPOSAL,
        handleShowLotAndProposalEvent,
      );

      document.removeEventListener(
        AnalysisReportEvents.SHOW_NEXT_STEPS,
        handleShowNextStepsEvent,
      );
    };
  }, [setSearchParams]);

  const handleClickMenuItem = (menuItem: AnalysisSidebarMenuItems) => {
    setSearchParams((prev) => {
      prev.set("selectedMenuItem", menuItem);
      return prev;
    });
  };

  return (
    <AnalysisSidebar
      configPrefix="enquiry"
      conversation={conversation}
      loading={isReportGenerating || isReportReGenerating}
      noticeboardData={noticeboardData}
      onClickMenuItem={handleClickMenuItem}
      selectedMenuItem={selectedMenuItem}
      subTitle={subTitle}
      title={title}
    />
  );
}
